import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { toast } from "react-toastify";
import { PencilIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Member, MemberUpdateDto } from "../../types/device";

interface MemberFormValues {
  email: string;
  access: "Admin" | "User";
}

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  access: Yup.string()
    .oneOf(["Admin", "User"], "Access level must be Admin or User")
    .required("Access level is required"),
});

interface EditMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: Member | null;
  onSubmit: (memberId: string, memberData: MemberUpdateDto) => void;
  isLoading?: boolean;
  isLastAdmin?: boolean;
}

export default function EditMemberModal({
  isOpen,
  onClose,
  member,
  onSubmit,
  isLoading = false,
  isLastAdmin = false,
}: EditMemberModalProps) {
  if (!member) return null;

  const initialValues: MemberFormValues = {
    email: member.email,
    access: member.access,
  };

  const handleSubmit = (values: MemberFormValues) => {
    if (isLastAdmin && values.access === "User") {
      toast.error("Cannot demote the only admin. Assign another admin first.");
      return;
    }

    const updatedData: MemberUpdateDto = {};

    if (values.email !== member.email) {
      updatedData.email = values.email;
    }

    if (values.access !== member.access) {
      updatedData.access = values.access;
    }

    if (Object.keys(updatedData).length === 0) {
      toast.info("No changes to save");
      onClose();
      return;
    }

    onSubmit(member.id, updatedData);
  };

  return (
    <Transition appear show={isOpen}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/50" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                      <PencilIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                      Edit Member
                    </h3>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ isSubmitting }) => (
                    <Form className="space-y-4">
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                        >
                          Email Address *
                        </label>
                        <Field
                          type="email"
                          name="email"
                          id="email"
                          className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white sm:text-sm"
                          placeholder="Enter email address"
                        />
                        <ErrorMessage
                          name="email"
                          component="div"
                          className="mt-1 text-sm text-red-600"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="access"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                        >
                          Access Level *
                        </label>
                        <Field
                          as="select"
                          name="access"
                          id="access"
                          className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white sm:text-sm"
                        >
                          <option value="User" disabled={isLastAdmin}>
                            User
                          </option>
                          <option value="Admin">Admin</option>
                        </Field>
                        <ErrorMessage
                          name="access"
                          component="div"
                          className="mt-1 text-sm text-red-600"
                        />
                        {isLastAdmin && (
                          <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                            This member is the only admin. Make someone else an
                            admin before changing this role.
                          </p>
                        )}
                      </div>

                      <div className="mt-6 flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={onClose}
                          className="inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                          disabled={isSubmitting || isLoading}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="inline-flex justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={isSubmitting || isLoading}
                        >
                          {isSubmitting || isLoading
                            ? "Saving..."
                            : "Save Changes"}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
